/**
 * Deferred Analytics Loader
 * 
 * Loads Google Analytics and PostHog only after initial render in production.
 * Provides lightweight tracking without blocking initial page load.
 */

import { loadScriptOnIdle } from './scriptLoader';

const GA_MEASUREMENT_ID = 'G-Q8P5H1EJ9R';
const POSTHOG_API_KEY = 'phc_xAvL2Iq4tFmANRE7kzbKwaSqp1HJjN7x48s3vr0CMjs';
const POSTHOG_API_HOST = 'https://us.i.posthog.com';

let analyticsInitialized = false;
let posthogInitialized = false;
let queuedEvents = [];

/**
 * Initialize Google Analytics (deferred)
 */
export async function initAnalytics() {
  // Skip in development
  if (process.env.NODE_ENV === 'development') {
    console.log('[Analytics] Skipped in development');
    return;
  }

  // Skip if already initialized
  if (analyticsInitialized) {
    return;
  }

  try {
    // Load gtag.js on idle
    await loadScriptOnIdle(
      `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`,
      { 
        async: true,
        id: 'gtag-script'
      },
      2000 // 2 second timeout
    );

    // Initialize gtag
    window.dataLayer = window.dataLayer || [];
    function gtag() { window.dataLayer.push(arguments); }
    window.gtag = gtag;

    gtag('js', new Date());
    gtag('config', GA_MEASUREMENT_ID, {
      send_page_view: false, // We'll send manually
      anonymize_ip: true
    });

    analyticsInitialized = true;

    // Flush queued events
    queuedEvents.forEach(event => {
      gtag('event', event.name, event.params);
    });
    queuedEvents = [];

    console.log('[Analytics] Google Analytics initialized');
  } catch (error) {
    console.error('[Analytics] Failed to load:', error);
  }
  
  // Also initialize PostHog
  initPostHog();
}

/**
 * Initialize PostHog (deferred)
 */
async function initPostHog() {
  if (process.env.NODE_ENV === 'development' || posthogInitialized) {
    return;
  }

  try {
    await loadScriptOnIdle(
      `https://us-assets.i.posthog.com/static/array.js`,
      { 
        async: true,
        id: 'posthog-script'
      },
      3000 // 3 second timeout
    );

    if (window.posthog) {
      window.posthog.init(POSTHOG_API_KEY, {
        api_host: POSTHOG_API_HOST,
        person_profiles: 'identified_only',
        session_recording: {
          recordCrossOriginIframes: true,
          capturePerformance: false,
        },
        loaded: () => {
          posthogInitialized = true;
          console.log('[Analytics] PostHog initialized');
        }
      });
    }
  } catch (error) {
    console.error('[Analytics] PostHog failed to load:', error);
  }
}

/**
 * Track page view
 */
export function trackPageView(path = window.location.pathname) {
  if (process.env.NODE_ENV === 'development') {
    console.log('[Analytics] PageView:', path);
    return;
  }

  if (!analyticsInitialized) {
    queuedEvents.push({ name: 'page_view', params: { page_path: path } });
    return;
  }

  if (window.gtag) {
    window.gtag('event', 'page_view', { page_path: path });
  }
}

/**
 * Track custom event
 */
export function trackEvent(eventName, params = {}) {
  if (process.env.NODE_ENV === 'development') {
    console.log('[Analytics] Event:', eventName, params);
    return;
  }

  if (!analyticsInitialized) {
    queuedEvents.push({ name: eventName, params });
    return;
  }

  if (window.gtag) {
    window.gtag('event', eventName, params);
  }
}

export default {
  initAnalytics,
  trackPageView,
  trackEvent
};
